import styles from './RegisterBanner.module.css';

import type { FC } from 'react';

import { useNavigate } from 'react-router';
import { useSyncExternalStore } from 'react';
import { X, ArrowRight } from '@phosphor-icons/react';

import { Stack } from 'src/components/themed/Stack';
import { Typography } from 'src/components/themed/Typography';

const DISMISSED_KEY = 'gac:register-banner-dismissed';
const DISMISSED_EVENT = 'gac:register-banner-dismissed';

// A browser that blocks storage cannot remember the dismissal — and must not lose the catalog
// over a banner: the read is on the hydration path of every anonymous visitor.
const isDismissed = (): boolean => {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
};

const onDismissal = (callback: () => void): (() => void) => {
  window.addEventListener(DISMISSED_EVENT, callback);
  window.addEventListener('storage', callback);

  return () => {
    window.removeEventListener(DISMISSED_EVENT, callback);
    window.removeEventListener('storage', callback);
  };
};

/**
 * Non-intrusive nudge toward the plans page, shown only to fully anonymous visitors
 * (no wallet session, no account). Dismissible and remembered — the soft-migration
 * banner from §5, deliberately not a wall. Existing wallet users never see it.
 *
 * Whether it was dismissed is the browser's knowledge (`localStorage`). The server renders the
 * banner — the visitor it is for is the one who has never been here — and a visitor who dismissed
 * it loses it again as soon as the browser has the page.
 */
export const RegisterBanner: FC = () => {
  const navigate = useNavigate();
  const dismissed = useSyncExternalStore(onDismissal, isDismissed, () => false);

  if (dismissed) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    window.dispatchEvent(new Event(DISMISSED_EVENT));
  };

  return (
    <div className={styles.banner}>
      <button className={styles.content} onClick={() => navigate('/plans')} type="button">
        <Stack gap={1} alignItems="flex-start">
          <Typography color="white" fontWeight="600" variant="large" textAlign="left">
            New to GAC?
          </Typography>
          <Typography color="lightWhite" variant="small" textAlign="left">
            Create a free account to buy, rent and track your deals.
          </Typography>
        </Stack>
        <ArrowRight size={20} color="#CCFF00" weight="bold" />
      </button>
      <button className={styles.close} onClick={dismiss} type="button" aria-label="Dismiss">
        <X size={16} color="rgba(255,255,255,0.6)" />
      </button>
    </div>
  );
};
